import { useEffect, useRef, useState } from "react";
import Auth from "../Auth";
import { createGrowthBook } from "../lib/growthbookClient";
import { shouldRecordGrowthBookExperimentView } from "../lib/growthbookExperimentTracking";

export default function useGrowthbook({ clientId, history, user, loadedUser }) {
  const initialized = useRef(false);
  const featuresLoadedForUserId = useRef(null);
  const userRef = useRef(user);
  const clientIdRef = useRef(clientId);
  const [growthbook, setGrowthbook] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  userRef.current = user;
  clientIdRef.current = clientId;

  const userId = user?.uid ?? null;

  useEffect(() => {
    if (!clientId || !loadedUser || initialized.current) {
      return;
    }

    const gb = createGrowthBook({
      trackingCallback: (experiment, result) => {
        const uid = userRef.current?.uid;
        if (!shouldRecordGrowthBookExperimentView(experiment.key, uid)) {
          return;
        }

        Auth.fetchWithIdToken("/api/test/experimentViewed", {
          experiment_id: experiment.key,
          user_id: uid,
          variation_id: result.key,
          anonymous_id: clientIdRef.current,
        });
      },
    });

    gb.setAttributes({
      deviceId: clientId,
    });
    gb.setURL(window.location.href);

    initialized.current = true;
    setGrowthbook(gb);
  }, [clientId, loadedUser]);

  useEffect(() => {
    if (!growthbook || !userId) {
      setAuthReady(false);
      return;
    }

    growthbook.setAttributes({
      id: userId,
      deviceId: clientId,
    });

    if (featuresLoadedForUserId.current === userId) {
      setAuthReady(true);
      return;
    }

    let cancelled = false;
    setAuthReady(false);

    Promise.resolve(growthbook.loadFeatures()).then(() => {
      if (cancelled) {
        return;
      }
      featuresLoadedForUserId.current = userId;
      setAuthReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [growthbook, userId, clientId]);

  useEffect(() => {
    if (!growthbook?.setURL) return;
    return history.listen((location) => {
      growthbook.setURL(window.location.origin + location.pathname);
    });
  }, [history, growthbook]);

  return { growthbook, authReady };
}
